import { Box, TextField, Tooltip } from '@atgfr8/atg-ui-tailwind';
import { SelectInput } from '@components';
import {
  customerDetailsStore,
  updateCustomerDetailsState,
} from '@store/customers/customerDetails';
import { InformationIcon, WebIcon } from '@atgfr8/icons';
import { formatDataTestId } from '@utils/dataTestIdFormatter';
import { isAdmin, isSuperAdmin } from '@utils/utils';
import { createSignal, Show } from 'solid-js';

import {
  CustomerSeasonalityList,
  CustomerStructureList,
} from '../../constants';
import { additionalCustomerDetailsClasses as classes } from './classes';

const AdditionalCustomerDetails = () => {
  const isCustomerReadOnly = () => {
    return !(isAdmin() || isSuperAdmin());
  };

  const [emailDomainWarning, setEmailDomainWarning] = createSignal<
    string | null
  >(null);

  const validateEmailDomain = (value: string) => {
    if (!value) {
      setEmailDomainWarning(null);
      return;
    }
    // Allow alphanumeric characters, dots, and hyphens only
    const domainRegex =
      /^(?:[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?\.)+[a-zA-Z]{2,}$/;
    if (!domainRegex.test(value)) {
      setEmailDomainWarning(
        'Please enter a valid domain (e.g., customer.com).',
      );
    } else {
      setEmailDomainWarning(null);
    }
  };

  return (
    <Box class={classes.container}>
      <Box class="w-full mb-3">
        <TextField
          label="Customer Email Domain"
          placeholder="Enter Customer Email Domain (e.g., customer.com)"
          value={customerDetailsStore.customer.emailDomain ?? ''}
          onChange={(val: string) => {
            updateCustomerDetailsState({ emailDomain: val });
            validateEmailDomain(val);
          }}
          startIcon={<WebIcon size={'20px'} color="#026EA1" />}
          class="w-full"
          disabled={isCustomerReadOnly()}
          error={!!emailDomainWarning()}
          maxLength={255}
          dataTestId="additional-customer-details-email-domain-textInput"
        />
        <Show when={emailDomainWarning()}>
          <span class="text-xs text-[#f50000] mt-1">
            {emailDomainWarning()}
          </span>
        </Show>
      </Box>
      <SelectInput
        label="Customer Structure"
        menuItems={CustomerStructureList.map((item) => ({
          label: item.label,
          value: String(item.value),
        }))}
        onChange={(val) =>
          updateCustomerDetailsState({
            isCustomerCentralized: val === 'null' ? null : val === 'true',
          })
        }
        value={String(customerDetailsStore.customer.isCustomerCentralized)}
        disabled={isCustomerReadOnly()}
        dataTestId={formatDataTestId('Customer Structure', 'selectInput')}
      />
      <Box class={classes.headerContainer}>
        <SelectInput
          label="Customer Seasonality"
          menuItems={CustomerSeasonalityList.map((item) => ({
            label: item.label,
            value: String(item.value),
          }))}
          onChange={(val) =>
            updateCustomerDetailsState({
              seasonality: val === 'null' ? null : val === 'true',
              seasonalityComment: '',
            })
          }
          value={String(customerDetailsStore.customer.seasonality)}
          disabled={isCustomerReadOnly()}
          dataTestId={formatDataTestId('Customer Seasonality', 'selectInput')}
        />
        <Tooltip
          placement="left"
          class="z-50!"
          title='Seasonal customers are businesses whose product demand changes based on the season (i.e.: Produce which has a more active season from February to July). If an agent believes they have a "seasonal" customer, the agent must communicate with the credit team who will review the customer information to determine whether or not a customer is in fact considered "seasonal".'
          dataTestId="additional-customer-details-seasonality-tooltip"
        >
          <InformationIcon class={classes.infoTooltip} />
        </Tooltip>
      </Box>
      <TextField
        multiline
        label="Seasonality Comment"
        rows={6}
        placeholder="Enter your message here."
        onChange={(data) =>
          updateCustomerDetailsState({
            seasonalityComment: data,
          })
        }
        disabled={isCustomerReadOnly()}
        maxLength={150}
        dataTestId="additional-customer-details-seasonality-comment-textAreaField"
      />
    </Box>
  );
};

export default AdditionalCustomerDetails;
